import { Link as TanStackLink } from '@tanstack/react-router'
import { ComponentProps, PropsWithChildren, forwardRef } from 'react'
import styled from 'styled-components'
import { FlexProps, flex, variant } from 'styled-system'
import useNavigation from '~/hooks/useNavigation'

import { AnalyticsQueryFilter } from '@hap/analytics'
import { cn } from '@hap/components'
import { Timespan } from '@hap/utils'

interface LinkState {
  query?: AnalyticsQueryFilter[]
  timespan?: Timespan.Info
}

interface RouteProps extends FlexProps {
  to: string
  drilldown?: boolean
  variant?: string
  disabled?: boolean
  className?: string
  state?: LinkState | unknown
}

type AProp = ComponentProps<'a'>

type Props = RouteProps | AProp

const isA = (props: Props): props is AProp => !('to' in props)
const isLink = (props: Props): props is RouteProps => 'to' in props

const RouteLink = forwardRef<HTMLAnchorElement, PropsWithChildren<RouteProps>>(
  ({ drilldown, state, ...props }, ref) => {
    const { buildPath, buildDrilldownPath, buildDrilldownState } = useNavigation()
    const linkState = state as LinkState | undefined
    const resolvedPath = buildDrilldownState(
      drilldown ? buildDrilldownPath(props.to) : buildPath(props.to),
      linkState?.query,
      linkState?.timespan,
    )
    const to =
      typeof resolvedPath === 'string' ? resolvedPath : resolvedPath.pathname || '/'

    return (
      <TanStackLink
        {...props}
        ref={ref}
        className={cn(props.className, {
          'pointer-events-none opacity-50': props.disabled,
        })}
        to={to}
      />
    )
  },
)

const Link = forwardRef<HTMLAnchorElement, PropsWithChildren<Props>>((props, ref) => {
  if (isA(props))
    return (
      <a {...props} ref={ref}>
        {props.children}
      </a>
    )
  if (isLink(props)) return <RouteLink {...props} ref={ref} />
  throw new Error('invalid props provided to <Link />')
})

export default styled(Link)(
  {
    // color: themeGet('colors.text'),
    cursor: 'pointer',
    textDecoration: 'none',
    lineHeight: '20px',
  },
  flex,
  variant({
    prop: 'variant',
    variants: {
      primary: {
        color: 'primary',
      },
      secondary: {
        color: 'link-secondary',
        borderBottomWidth: '1px',
        borderBottomStyle: 'dashed',
        borderBottomColor: 'accent6',
      },
      light: {
        'color': 'text-secondary',

        '&:hover': {
          color: 'text',
        },
      },

      danger: {
        'color': 'text-secondary',

        '&:hover': {
          color: 'error-text',
        },
      },
    },
  }),
)
